import {
	addMilliseconds,
	differenceInMilliseconds,
	isAfter,
	isSameDay,
	isValid,
	addDays,
	isBefore,
	differenceInSeconds,
	startOfYear,
} from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { EventDto, EventInstanceDto, UpdateEventDto } from '../models/dto';
import { DEFAULT_TIME_ZONE, defaultEventDuration } from '../constants';
import { getLocalTimeZone, getTimeZoneAbbrAndOffset } from './time-zones.utils';
import { merge } from 'lodash';

export const getEventShortLocalInstance = (event: EventDto, instance: Date | number | string) =>
	formatInTimeZone(new Date(instance), event.tzid ?? DEFAULT_TIME_ZONE, 'yyyy-MM-dd');

export const getEventEndDate = (event?: EventInstanceDto): Date | undefined => {
	if (!event?.endDate) {
		return;
	}

	return getOrCreateEventEndDate(event);
};

export const getOrCreateEventEndDate = (eventData: EventInstanceDto): Date | undefined => {
	if (!eventData) {
		return;
	}

	// get end date or add 2 hours to instance (or start date)
	const diff = eventData.endDate && isAfter(new Date(eventData.endDate), new Date(eventData.startDate))
		? differenceInMilliseconds(new Date(eventData.endDate), new Date(eventData.startDate))
		: defaultEventDuration;

	return addMilliseconds(new Date(eventData.instance ?? eventData.startDate), diff);
};

export const isEventPast = (eventData: EventInstanceDto): boolean => {
	if (!eventData) {
		return false;
	}

	const endDate = getOrCreateEventEndDate(eventData);

	return !!endDate && isBefore(endDate, new Date());
};

export const canCheckoutEventAfterEndDate = (eventData: EventInstanceDto): boolean => {
	if (!eventData?.instance || !eventData?.shortLocalInstance) return false;

	const endDate = getOrCreateEventEndDate(eventData);

	if (typeof endDate === 'undefined') {
		return false;
	}

	const allowCheckoutAfterEndDate = addDays(endDate, eventData.allowCheckoutForNDaysAfterEventEnded ?? 1);

	return !isBefore(allowCheckoutAfterEndDate, new Date());
};

export const getTicketSalesStartDateTime = (eventData?: Pick<EventInstanceDto, 'additionalData'>): Date | null => {
	const salesStartTimeIsoString = eventData?.additionalData?.ticketSalesStartUtcTimeIso;

	if (!salesStartTimeIsoString?.length) {
		return null;
	}

	return new Date(salesStartTimeIsoString);
};

export const getSecondsToTicketsSalesStart = (eventData?: Pick<EventInstanceDto, 'additionalData'>): number | null => {
	const ticketSalesStart = getTicketSalesStartDateTime(eventData);

	if (!ticketSalesStart) {
		return null;
	}

	return differenceInSeconds(ticketSalesStart, new Date());
};

export type FormatEventTimeRangeOptions = {
	timezone?: string;
	startDateFormat?: string;
	endDateFormat?: string;
	timeFormat?: string;
	separator?: string;
	showTimezone?: boolean;
}

export const formatEventTimeRange = (
	startDate: Date | number | string,
	endDate?: Date | number | string | null,
	options?: FormatEventTimeRangeOptions
): string => {
	if (!isValid(new Date(startDate))) {
		return '';
	}

	const {
		startDateFormat = 'eee, LLL do, h:mm aa',
		endDateFormat = 'eee, LLL do, h:mm aa',
		timeFormat = 'h:mm aa',
		separator = '-',
		timezone = getLocalTimeZone(),
		showTimezone = true,
	} = options ?? {};

	const parsedStartDate = typeof startDate === 'string' ? new Date(startDate) : startDate;
	const parsedEndDate = typeof endDate === 'string' ? new Date(endDate) :	endDate;
	const timezoneSuffix = showTimezone ? ` ${getTimeZoneAbbrAndOffset(new Date(parsedStartDate), timezone).abbr}` : '';

	if (!parsedEndDate) {
		return `${formatInTimeZone(parsedStartDate, timezone, startDateFormat)}${timezoneSuffix}`;
	}

	const formatedStartDate = formatInTimeZone(parsedStartDate, timezone, startDateFormat);
	const formatedEndDate = formatInTimeZone(
		parsedEndDate,
		timezone,
		isSameDay(utcToZonedTime(parsedStartDate, timezone), utcToZonedTime(parsedEndDate, timezone)) ? timeFormat : endDateFormat
	);

	return `${formatedStartDate} ${separator} ${formatedEndDate}${timezoneSuffix}`;
};

export const prepareUpdateEventMinimalPayload = (
	eventInstance: EventInstanceDto,
	updateData: UpdateEventDto
): Partial<UpdateEventDto> => {
	const { id, instance, startDate, additionalData } = eventInstance;

	return merge(
		{
			id,
			instance: instance ? new Date(instance) : undefined,
			startDate: startDate ? new Date(startDate) : undefined,
			additionalData,
			// TODO add other required fields from event data
		},
		updateData
	);
};
